import { backendApi } from "@/services/backend/api";
import {
  Fulfillment,
  FulfillmentState,
} from "@/services/backend/relationships/types/fulfillment";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { ServiceKind } from "@bitory-gmbh/bsm";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

const fulfillmentApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    relationshipsFulfillmentCreate: build.mutation<Fulfillment, CreateRequest>({
      query: (request) => ({
        url: `relationships/fulfillment`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RelationshipsFulfillment", id: request.id },
        { type: "RelationshipsFulfillment", id: "LIST" },
      ],
    }),
    relationshipsFulfillmentShow: build.query<Fulfillment, ShowRequest>({
      query: (request) => ({
        url: `relationships/fulfillment`,
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _e, request) => [
        { type: "RelationshipsFulfillment", id: request.id },
      ],
    }),
    relationshipsFulfillmentList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `relationships/fulfillment/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        providesList(result?.fulfillments, "RelationshipsFulfillment"),
    }),
    relationshipsFulfillmentDelete: build.mutation<void, DeleteRequest>({
      query: (request) => ({
        url: `relationships/fulfillment`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: [
        "RelationshipsFulfillment",
        { type: "RelationshipsFulfillment", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useRelationshipsFulfillmentCreateMutation,
  useRelationshipsFulfillmentShowQuery,
  useRelationshipsFulfillmentListQuery,
  useRelationshipsFulfillmentDeleteMutation,
} = fulfillmentApi;

export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  branchId: UUID;
  relationshipId: UUID;
  commitmentId: UUID;
  processId?: UUID;
  serviceKind: ServiceKind;
  plannedDate: RFC3339timestamp;
}

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  commitment?: Filter<UUID>;
  process?: Filter<UUID>;
  serviceKind?: Filter<ServiceKind>;
  plannedDate?: Filter<RFC3339timestamp>;
  state?: Filter<FulfillmentState>;
  orderBy?: OrdrBy<FulfillmentOrderBy>[];
  limit?: number;
  offset?: number;
}

export type FulfillmentOrderBy =
  "plannedDate" | "state" | "createdAt" | "updatedAt";

export interface List {
  fulfillments: Fulfillment[];
  count: number;
  offset: number;
  totalCount: number;
}

export interface DeleteRequest {
  id: UUID;
}
